import React, { useState } from 'react'
import { Box, Button, Text } from '@indeed/ifl-components'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'

import { SchedulingPlanDialog, useSchedulingPlan } from 'syft-acp-core/components/SchedulingPlanDialog'

import type { BookingDeadlineKind } from './ShiftAgencyList.types'

const dateTimeFormat = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
} as const

type BookingPlanProps = {
  agencyName: string
  allocationId: number
  deadline: string
  deadlineKind: BookingDeadlineKind
  jobId: number
  planId: number
  venueTimezone: string
}

const deadlineHeadings: Record<BookingDeadlineKind, string> = {
  asap: 'Book as soon as possible',
  block: 'Block booking deadline',
  shift: 'Booking deadline',
}

export const BookingPlan = ({
  agencyName,
  allocationId,
  deadline,
  deadlineKind,
  jobId,
  planId,
  venueTimezone,
}: BookingPlanProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { requestPlan } = useSchedulingPlan(planId)
  const [, formatDateTime] = useFormatDateTime()
  const format = { ...dateTimeFormat, timeZone: venueTimezone }
  const formatDeadline = () =>
    deadline && !Number.isNaN(Date.parse(deadline)) ? formatDateTime(deadline, { format }) : deadline || '—'

  return (
    <>
      <Box>
        <Text as="strong" level={1} sx={{ display: 'block', textTransform: 'uppercase' }}>
          {deadlineHeadings[deadlineKind]}
        </Text>
        {deadlineKind === 'asap' ? (
          // The column does not wrap, so this would otherwise widen it past the heading.
          <Text as="div" sx={{ whiteSpace: 'normal' }}>
            (shift has no enforceable deadline)
          </Text>
        ) : (
          <Text as="div">{formatDeadline()}</Text>
        )}
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-label={`View plan for agency allocation ${allocationId}`}
          sx={{ fontSize: 2, fontWeight: 'normal' }}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            requestPlan()
            setIsOpen(true)
          }}
        >
          View plan
        </Button>
      </Box>

      {isOpen && (
        <SchedulingPlanDialog
          agencyName={agencyName}
          jobId={jobId}
          onOpenChange={setIsOpen}
          planId={planId}
          venueTimezone={venueTimezone}
        />
      )}
    </>
  )
}
